import { Component, Suspense } from 'react'
import Modal from '.'
import PopupModal from './PopupModal'
import { connect } from 'react-redux'
import { getNominative } from '../../common/utils'
import Field from '../fields'
import { history } from '../../store'

interface ResidentData {
  status: string
  name: {
    first: string
    last: string
  }
  gender: string
  dateOfBirth: string
  dols: boolean
  dnacpr: boolean
  fundingSource: string
}

interface AddResidentModalProps {
  createItem: (data: any) => void
  fetchList: (list: string) => void
  showToast: (options: { title: string }) => void
  features: any
  fields: ResidentData
  setFormField: (data: any) => void
  residents: any[]
  carePlans: any
  hasPro: boolean
  preferredPeopleNominative: string
  isOpen: boolean
}

export class AddResidentModal extends Component<AddResidentModalProps> {
  // eslint-disable-next-line react/state-in-constructor
  state = {
    statusStep: true
  }

  componentDidUpdate(prevProps: AddResidentModalProps) {
    const { features, fields, setFormField } = this.props
    if (features && fields && (!prevProps.features || !prevProps.fields)) {
      setFormField({
        key: 'status',
        value: 'active'
      })
    }
  }

  handleSubmit = (options: any) => {
    const { fields, setFormField, residents } = this.props
    const hasOneDummy =
      residents?.filter((item) => item?.status === 'dummy').length === 1
    const existingDummy = residents?.find((item) => item?.status === 'dummy')
    const dummyResident =
      dummyNames[hasOneDummy && existingDummy?.gender === 'male' ? 1 : 0]
    const isStatusStep = this.state.statusStep && options.length > 1
    if (fields?.status === 'dummy') {
      if (!fields?.name?.first) {
        setFormField({
          key: 'name.first',
          value: dummyResident.firstName
        })
      }
      if (!fields?.name?.last) {
        setFormField({
          key: 'name.last',
          value: dummyResident.lastName
        })
      }
      if (!fields?.gender) {
        setFormField({
          key: 'gender',
          value: dummyResident.gender
        })
      }
      if (!fields?.dateOfBirth) {
        setFormField({
          key: 'dateOfBirth',
          value: dummyResident.dateOfBirth
        })
      }
      if (!fields?.dols) {
        setFormField({
          key: 'dols',
          value: dummyResident.dols
        })
      }
      if (!fields?.dnacpr) {
        setFormField({
          key: 'dnacpr',
          value: dummyResident.dnacpr
        })
      }
      if (!fields?.fundingSource) {
        setFormField({
          key: 'fundingSource',
          value: dummyResident.fundingSource
        })
      }
    }
    this.setState({ statusStep: false })
    if (!isStatusStep) {
      this.props.createItem({
        url: 'list/residents',
        onComplete: this.handleComplete
      })
    }
  }

  handleComplete = (resident: any) => {
    const { fetchList, showToast, preferredPeopleNominative } = this.props
    fetchList('residents')
    history.push(`/residents/${resident.id}/profile`)
    this.setState({ statusStep: true })
    showToast({
      title: `Added a ${getNominative(preferredPeopleNominative, 'Resident')}`
    })
  }

  render() {
    const {
      fields,
      isOpen,
      carePlans,
      features,
      preferredPeopleNominative,
      residents,
      hasPro,
      ...formProps
    } = this.props
    const dummysAllowed =
      residents?.filter((item) => item?.status === 'dummy').length <= 1
    const isDummy = fields?.status === 'dummy'
    const options = [
      {
        label: 'Active',
        description:
          'Create a profile for a service user. Note: this will impact your bill.',
        value: 'active'
      }
    ]
    if (carePlans) {
      options.push({
        label: 'Initial Assessment',
        description:
          'Create a profile for a potential service user to assess their care needs.',
        value: 'pre-assessment'
      })
    }
    if (dummysAllowed) {
      options.push({
        label: 'Dummy Profile',
        description:
          'Create a fake profile to explore Log my Care without affecting your bill. ',
        value: 'dummy'
      })
    }
    if (!isOpen || !fields) return null
    const isStatusStep = this.state.statusStep && options.length > 1
    return (
      <PopupModal
        confirmText={isStatusStep ? 'Next' : 'Add'}
        buttonIcon={isStatusStep ? undefined : 'PlusOutlined'}
        buttonColour="success"
        list={getNominative(preferredPeopleNominative, 'Resident')}
        includesRequired={!isStatusStep}
        onConfirm={() => this.handleSubmit(options)}
        onClose={() => this.setState({ statusStep: true })}
        titleDescription={isStatusStep ? 'Set status' : undefined}
        type="create"
        {...formProps}
      >
        {isStatusStep ? (
          <Suspense fallback={<div>Loading...</div>}>
            <Field
              formId={MODAL_ID}
              keys="status"
              label="Current Status"
              options={options}
              supportLink={SUPPORT_LINK}
              required
              type="radios"
              values={fields}
              floatHelpLink
              withDescription
            />
          </Suspense>
        ) : (
          <>
            <div style={styles.fieldWrapper}>
              <Suspense fallback={<div>Loading...</div>}>
                <Field
                  formId={MODAL_ID}
                  keys={['name.first', 'name.last']}
                  label="Name"
                  placeholders={['First name', 'Last name']}
                  required
                  type="multiText"
                  values={fields}
                  disabled={isDummy}
                />
              </Suspense>
            </div>
            <div style={styles.fieldWrapper}>
              <Suspense fallback={<div>Loading...</div>}>
                <Field
                  formId={MODAL_ID}
                  keys="gender"
                  className="gender-field"
                  label="Gender"
                  options={[{ value: 'male' }, { value: 'female' }]}
                  required
                  type="radios"
                  values={fields}
                  disabled={isDummy}
                />
              </Suspense>
            </div>
            <div style={styles.fieldWrapper}>
              <Suspense fallback={<div>Loading...</div>}>
                <Field
                  className="birthdate-field"
                  formId={MODAL_ID}
                  keys="dateOfBirth"
                  label="Date of Birth"
                  placeholders="DD-MM-YYYY"
                  required
                  type="birthdate"
                  validationTypes="birthdate"
                  values={fields}
                  disabled={isDummy}
                />
              </Suspense>
            </div>
          </>
        )}
      </PopupModal>
    )
  }
}

const MODAL_ID = 'addResident'
const SUPPORT_LINK =
  'https://support.logmycare.co.uk/en/articles/3376538-how-do-i-use-initial-assessments'
const styles = {
  fieldWrapper: {
    marginBottom: 20
  }
}

const mapStateToProps = (state: any) => ({
  preferredPeopleNominative: state.global.home.preferredPeopleNominative,
  hasPro: state.global.home.pro, // feature flag
  residents: state.data.residents
})

const dummyNames = [
  {
    firstName: 'John',
    lastName: 'Q. Public',
    gender: 'male',
    dateOfBirth: '09-08-1982',
    dols: false,
    dnacpr: false,
    fundingSource: 'Local Authority'
  },
  {
    firstName: 'Jane',
    lastName: 'Smith',
    gender: 'female',
    dateOfBirth: '24-12-1962',
    dols: false,
    dnacpr: false,
    fundingSource: 'Local Authority'
  }
]

const wrappedModal = connect(mapStateToProps, null)(AddResidentModal)

export default Modal(wrappedModal, MODAL_ID)
